// cspell:ignore unprovisioned
/**
 * Sagas for the watermark library (VE-24693). Bridges request actions to the
 * SDO services and surfaces failure toasts. Re-fetches the list after a
 * successful create or update so the table stays in sync — the hard delete
 * needs no refetch, because the reducer drops the row on its own success.
 * Mirrors settingsProfile/sagas; forked from initMainPage + the doc-editor route.
 *
 * Fallback: DISABLED, but still wired. When the registry has no published
 * schema on this cluster the CRUD used to be served from a browser-local store
 * (localStore.ts) so the feature stayed usable before the registry existed
 * anywhere — a build crutch. `WATERMARK_LOCAL_FALLBACK_ENABLED` is now false, so
 * an unprovisioned registry FAILS the operation exactly as an unreachable one
 * does, `WatermarkUnavailableNotice` says the library is not set up, and Add New is
 * disabled so the failure is announced before an upload rather than after one.
 *
 * The branches are left in place behind that constant rather than deleted, so
 * the crutch is one flag away during rollout. Expect to strip them, the module
 * and the notice's local-only wording once the registry is live everywhere.
 *
 * Resolution, not configuration, is the test. The registry id is hard-coded in
 * `services/index.ts` (config overrides it), so "is an id set?" would now be
 * true everywhere and would say the SDO path is available on a cluster where
 * the registry does not exist yet. Asking whether a published schema actually
 * came back is the question that has always been meant here; the config check
 * was only ever a proxy for it.
 *
 * And three answers, not two. `unprovisioned` — the lookup succeeded and there
 * is no schema — is the only one that goes local. `unreachable` — the lookup
 * itself failed — FAILS the operation, because a transient 502 is not evidence
 * that shared storage does not exist, and treating it as such would write the
 * user's watermark into one browser while telling them it saved.
 */
import {
  all,
  call,
  delay,
  fork,
  put,
  putResolve,
  race,
  select,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { modules } from '@veritone/glc-redux';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { sagaIntl } from '@i18n';
import * as Actions from '../actions';
import * as Services from '../services';
import type { WatermarkRegistryResolution } from '../services';
import {
  serviceCreateWatermarkTdo,
  serviceDeleteWatermarkTdo,
  serviceSaveWatermarkAsset,
  STORE_WATERMARK_RASTER_IN_SDO,
  WatermarkAssetConfig,
  WATERMARK_RASTER_ASSET_TYPE,
  WATERMARK_SOURCE_IMAGE_ASSET_TYPE,
} from '../services/imageAssets';
import {
  selectGraphQLConfig,
  selectToken,
} from '@redact-modules/documents/sagas/auth';
import {
  selectAllWatermarkRecords,
  selectApplyingWatermarkIds,
} from '../selectors';
import {
  appliedFromDefinition,
  WatermarkDefinition,
  WatermarkDefinitionData,
  WatermarkDraft,
  FetchWatermarksResponse,
} from '../models';
import { hydrateDefinitionArtwork } from '../artwork';
import { deriveWatermarkImage } from '@redact-modules/documents/watermarkImage';
import {
  lsDeleteWatermark,
  lsListWatermarks,
  lsUpsertWatermark,
  WATERMARK_LOCAL_FALLBACK_ENABLED,
} from '../localStore';
import { ADD_WATERMARK } from '@redact-modules/documents/actions';

/**
 * The library is fetched in one request. Deliberate — the editor picker and the
 * settings table both want the whole list and there is no cursor UI — but the
 * returned `count` is checked so an org that outgrows this says so instead of
 * quietly showing a truncated library.
 */
const PAGE_SIZE = 1000;

export default function* watermarksSaga() {
  yield* all([
    fork(watchFetch),
    fork(watchCreate),
    fork(watchUpdate),
    fork(watchDelete),
    fork(watchApply),
  ]);
}

/** Shared by an apply whose artwork is missing and one that timed out. */
const artworkUnavailableToast = () =>
  enqueueSnackbar({
    message: sagaIntl().formatMessage({
      id: 'watermarkArtworkUnavailable',
      defaultMessage:
        'This watermark’s image could not be loaded, so it was not applied.',
    }),
    variant: 'error',
  });

/**
 * Resolve a library watermark's artwork, then put it on the document (§K).
 *
 * The picker cannot do this itself any more. It holds the row, and since the
 * raster moved to the watermark's TDO the row may carry only a 256px preview —
 * enough to choose from, not enough to stamp. So the fetch happens here and
 * `ADD_WATERMARK` is dispatched with a mark that can actually be drawn.
 *
 * Ordering matters for the document-side asset saga: it reacts to
 * `ADD_WATERMARK` by copying the raster onto the document's own TDO (§J), so
 * it must not see the action before the raster is in hand — an image mark with
 * no artwork is skipped there, silently, and the document would save a
 * reference to nothing.
 *
 * An image definition that resolves to no raster is NOT applied. A mark that
 * cannot be drawn is worse than no mark: it is invisible on the canvas, absent
 * from the export, and present in the saved body, so the operator has no way to
 * tell that the watermark they asked for is not on the document.
 */
export function* applyLibraryWatermark(
  action: PayloadAction<{
    instanceId: string;
    definitionId: string;
    fileOnly?: boolean;
  }>
): Generator<unknown, void, unknown> {
  const { instanceId, definitionId } = action.payload;
  const record = yield* findRecord(definitionId);
  if (!record) {
    return;
  }
  let data = record.data;
  try {
    const cfg = yield* assetConfig();
    data = yield* call(hydrateDefinitionArtwork, record.data, cfg);
  } catch (err) {
    console.warn('Watermark artwork could not be loaded', err);
  }
  if (data.type === 'image' && !data.imageBaseDataUrl) {
    yield* put(artworkUnavailableToast());
    return;
  }
  const applied = appliedFromDefinition(instanceId, record.id, data);
  if (applied.type === 'image' && applied.imageBaseDataUrl) {
    // Derived here, not left as the colour base. `appliedFromDefinition`
    // seeds `imageDataUrl` with the UNDERIVED base so a fresh mark is
    // visible immediately, and derivation otherwise only runs on document
    // open and on a grayscale/colour CHANGE — so a definition whose default
    // is grayscale arrived in colour and stayed that way until the operator
    // toggled the switch off and on again, which is the one action nobody
    // would think to perform on a setting that already looks correct in the
    // panel.
    //
    // `watermarkAutoApply` already did this for the path it drives. This is
    // the same fix for the path the operator drives.
    try {
      applied.imageDataUrl = yield* call(
        deriveWatermarkImage,
        applied.imageBaseDataUrl,
        { grayscale: applied.grayscale, color: applied.color }
      );
    } catch (err) {
      // Best-effort, matching the restore and auto-apply paths: the mark
      // keeps the colour base rather than not being applied at all.
      console.warn('Watermark image could not be derived', err);
    }
  }
  // Meta only when it SAYS something. `{ fileOnly: undefined }` is a
  // different effect from no meta at all, and every other caller of this path
  // has always dispatched without one.
  yield* put(
    ADD_WATERMARK(
      applied,
      action.payload.fileOnly ? { fileOnly: true } : undefined
    )
  );
}

/** How long one apply may take before it is abandoned (VE-32397). */
export const APPLY_LIBRARY_WATERMARK_TIMEOUT_MS = 30_000;

/**
 * ONE APPLY PER DEFINITION AT A TIME (VE-32397). An image's artwork fetch
 * leaves a window where the mark is not on the document yet, so the picker
 * still offered it and a second pick stamped it twice. The picker reads this
 * flag to say "(added)"; the guard here covers a pick that beats the
 * re-render. Cleared in `finally` so a failed fetch can be retried.
 *
 * BOUNDED, because the asset fetches have no timeout: a stalled one would
 * otherwise hold the flag, and the picker would show "(added)" for a mark on
 * no document until a reload. On timeout the apply is cancelled, so no late
 * mark lands.
 */
export function* applyLibraryWatermarkOnce(
  action: ReturnType<typeof Actions.APPLY_LIBRARY_WATERMARK>
): Generator<unknown, void, unknown> {
  const { definitionId } = action.payload;
  const applying = yield* select(selectApplyingWatermarkIds);
  if (applying.includes(definitionId)) {
    return;
  }
  yield* put(Actions.SET_WATERMARK_APPLYING({ definitionId, applying: true }));
  try {
    const { timedOut } = yield* race({
      applied: call(applyLibraryWatermark, action),
      timedOut: delay(APPLY_LIBRARY_WATERMARK_TIMEOUT_MS),
    });
    if (timedOut) {
      yield* put(artworkUnavailableToast());
    }
  } finally {
    yield* put(
      Actions.SET_WATERMARK_APPLYING({ definitionId, applying: false })
    );
  }
}

function* watchApply() {
  yield* takeEvery(Actions.APPLY_LIBRARY_WATERMARK, applyLibraryWatermarkOnce);
}

/**
 * Where this operation's data lives — resolved, not guessed.
 *
 * `putResolve`, not `put`: redux-saga only awaits a thunk's promise when the
 * effect carries a resolver, so a plain `put` returns in the same tick and any
 * `select` after it reads pre-request state. An earlier version of this decided
 * the fallback that way, which made the local-store branch unreachable and let
 * a create dispatch into the void with the drawer's Save button latched on.
 *
 * Three outcomes, because two is what caused the damage: a request that FAILED
 * must not be read as a cluster that has no registry. On `unreachable` the
 * caller fails the operation and the user retries — writing to the local store
 * there would quietly succeed into somewhere nobody else can see.
 */
function* resolveRegistry(): Generator<
  unknown,
  WatermarkRegistryResolution,
  unknown
> {
  const resolution = (yield* putResolve(
    Services.resolveWatermarkRegistry() as never
  )) as unknown as WatermarkRegistryResolution;

  // Non-sticky, and cleared on success: the banner has to be able to go away
  // when the registry starts answering, or a single blip mislabels the session.
  yield* put(
    Actions.SET_WATERMARK_REGISTRY_UNAVAILABLE({
      unavailable: resolution.status === 'unprovisioned',
    })
  );

  if (resolution.status === 'unprovisioned' && !warnedAboutRegistry) {
    warnedAboutRegistry = true;
    // Loud once per session. The user-facing half of this is
    // `WatermarkUnavailableNotice`, which says the feature is not set up; this half
    // says WHY, to whoever has a console open, because "not set up" from the
    // app looks identical to a permissions problem or a bad config override.
    console.warn(
      '[redact] The watermark registry has no published schema on this ' +
        'cluster, so the library cannot be read or written: every watermark ' +
        'operation will fail and Add New is disabled. Publish the schema — ' +
        'see docs/schemas/WatermarkLibrarySchema.md.'
    );
  }
  return resolution;
}

/**
 * Report a failed operation the same way for every writer.
 *
 * `unreachable` used to fall through to the local store, which is the one
 * response that loses work silently: the write appears to succeed and lands
 * somewhere the org cannot read.
 */
function* failUnreachable(
  action: ReturnType<typeof Actions.CREATE_WATERMARK_FAILURE>
): Generator<unknown, void, unknown> {
  yield* put(action);
}

let warnedAboutRegistry = false;

function* refetch() {
  yield* put(Actions.FETCH_WATERMARKS_REQUEST({ limit: PAGE_SIZE, offset: 0 }));
}

function* watchFetch() {
  yield* all([
    takeLatest(
      Actions.FETCH_WATERMARKS_REQUEST,
      function* (
        action: PayloadAction<{
          limit: number;
          offset: number;
          pickableOnly?: boolean;
        }>
      ) {
        const { limit, offset, pickableOnly } = action.payload;
        const registry = yield* resolveRegistry();
        if (registry.status === 'ready') {
          yield* put(Services.fetchWatermarks(limit, offset, pickableOnly));
          return;
        }
        if (
          registry.status === 'unreachable' ||
          !WATERMARK_LOCAL_FALLBACK_ENABLED
        ) {
          // An empty list served confidently is worse than a failure: it reads
          // as "your org has no watermarks". With the fallback off, an
          // unprovisioned registry fails the same way an unreachable one does.
          yield* put(Actions.FETCH_WATERMARKS_FAILURE(undefined, undefined));
          return;
        }
        // Disabled path — see WATERMARK_LOCAL_FALLBACK_ENABLED. Serves
        // everything and lets the selectors filter; `pickableOnly` is an
        // optimisation on a round trip, and there is none here.
        const records = yield* call(lsListWatermarks);
        yield* put(
          Actions.FETCH_WATERMARKS_SUCCESS(
            { structuredDataObjects: { count: records.length, records } },
            undefined
          )
        );
      }
    ),
    takeEvery(
      Actions.FETCH_WATERMARKS_SUCCESS,
      function* (action: PayloadAction<FetchWatermarksResponse>) {
        const { records } = action.payload.structuredDataObjects;
        // Compared against the PAGE SIZE, not against the response's own
        // `count`. That is not a nicety: `count` is the number of records in
        // the page, not the number that matched — with `limit: 1` on a registry
        // holding six, it comes back as 1 — so `count > records.length` is
        // never true and this warning could not fire at all. An org that
        // outgrew PAGE_SIZE got a silently truncated library, which is the one
        // thing the check exists to prevent.
        //
        // A full page is not proof of truncation (an org with exactly
        // PAGE_SIZE watermarks is indistinguishable), so this says "may".
        if (records.length >= PAGE_SIZE) {
          // One page, no cursor UI.
          console.warn(
            `[redact] Watermark library returned a full page of ${PAGE_SIZE} ` +
              'definitions; any beyond that are not shown.'
          );
        }
        // Nothing to do on this branch; the yield exists only because a saga
        // must yield. `require-yield` is the rule, not a hint that work is
        // missing here.
        yield* call(() => undefined);
      }
    ),
    takeLatest(Actions.FETCH_WATERMARKS_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'fetchWatermarksFailure',
            defaultMessage:
              'Unexpected error occurred while fetching watermarks.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}

/** Auth + endpoint for the artwork services, which are plain async functions. */
function* assetConfig(): Generator<unknown, WatermarkAssetConfig, unknown> {
  const token = yield* select(selectToken);
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    yield* select(selectGraphQLConfig);
  return { token, apiRoot, graphQLEndpoint, veritoneAppId };
}

/**
 * Put an image definition's artwork on its own TDO and return the row to save
 * (§K). Text definitions and re-saves of an already-provisioned row pass
 * straight through.
 *
 * Runs BEFORE the SDO write, deliberately. The failure this ordering chooses is
 * an orphan TDO — invisible to every surface in this app, costing storage and
 * nothing else. The other ordering's failure is a live library row pointing at
 * artwork that does not exist, which is a broken watermark an operator can see,
 * pick, and apply to a disclosure document.
 *
 * A throw here therefore fails the whole save: better no row than a row that
 * cannot be drawn.
 *
 * The raster is dropped from the row unless the revert switch says otherwise —
 * that drop is the entire payload win, so it happens here, at the single point
 * every write passes through, rather than being left to each caller to remember.
 */
function* provisionArtwork(
  data: WatermarkDraft
): Generator<unknown, WatermarkDefinitionData, unknown> {
  if (data.type !== 'image' || !data.imageBaseDataUrl || data.imageTdoId) {
    const { sourceDataUrl: _unused, ...stored } = data;
    return stored;
  }
  const cfg = yield* assetConfig();
  const tdoId = yield* call(serviceCreateWatermarkTdo, data.watermarkName, cfg);
  yield* call(
    serviceSaveWatermarkAsset,
    tdoId,
    WATERMARK_RASTER_ASSET_TYPE,
    data.imageBaseDataUrl,
    'watermark.png',
    cfg
  );
  // The one point the upload's own bytes move from the form to the TDO.
  const source = data.sourceDataUrl;
  if (source) {
    yield* call(
      serviceSaveWatermarkAsset,
      tdoId,
      WATERMARK_SOURCE_IMAGE_ASSET_TYPE,
      source,
      data.imageFileName || 'watermark-source',
      cfg
    );
  }
  // Destructured out rather than set to undefined: `sourceDataUrl` lives on the
  // DRAFT only, so the row handed to the registry does not have the key at all.
  const { sourceDataUrl: _uploaded, ...stored } = data;
  return {
    ...stored,
    imageTdoId: tdoId,
    imageBaseDataUrl: STORE_WATERMARK_RASTER_IN_SDO
      ? data.imageBaseDataUrl
      : undefined,
  };
}

function* watchCreate() {
  yield* all([
    takeLatest(
      Actions.CREATE_WATERMARK_REQUEST,
      function* (action: PayloadAction<{ data: WatermarkDraft }>) {
        // The registry is resolved BEFORE the artwork is provisioned, and the
        // order is load-bearing. `provisionArtwork` creates a TDO and uploads
        // two assets to it; a row referencing that TDO is the only thing that
        // makes it reachable afterwards, because a watermark TDO deliberately
        // has no parent folder and no redact-state SDO, so it appears in
        // neither the dashboard nor Trash. Provisioning first and then failing
        // the row write leaves the operator's upload sitting in org storage
        // with nothing pointing at it and no way to delete it — once per
        // attempt, and an operator who has just been told "could not be
        // created" retries.
        //
        // It cost nothing to get right: the registry check is a cached lookup
        // and the upload is the expensive half.
        const registry = yield* resolveRegistry();
        if (registry.status !== 'ready' && !WATERMARK_LOCAL_FALLBACK_ENABLED) {
          yield* failUnreachable(
            Actions.CREATE_WATERMARK_FAILURE(undefined, undefined)
          );
          return;
        }
        let provisioned: WatermarkDefinitionData;
        try {
          provisioned = yield* provisionArtwork(action.payload.data);
        } catch (err) {
          console.warn('Watermark artwork could not be stored', err);
          yield* put(Actions.CREATE_WATERMARK_FAILURE(undefined, undefined));
          return;
        }
        const data = yield* stampWriteDefaults(provisioned);
        if (registry.status === 'ready') {
          yield* put(Services.createWatermark(data));
          return;
        }
        if (registry.status === 'unreachable') {
          yield* failUnreachable(
            Actions.CREATE_WATERMARK_FAILURE(undefined, undefined)
          );
          return;
        }
        // Disabled path. try/catch because the local store can genuinely fail
        // (quota), and reporting SUCCESS on a discarded write is worse than the
        // write failing: the drawer closes and the row is silently missing.
        try {
          const record = yield* call(
            lsUpsertWatermark,
            data,
            undefined,
            uuid(),
            new Date().toISOString()
          );
          yield* put(
            Actions.CREATE_WATERMARK_SUCCESS(
              { createStructuredData: record },
              undefined
            )
          );
        } catch (err) {
          console.warn('Watermark could not be saved locally', err);
          yield* put(Actions.CREATE_WATERMARK_FAILURE(undefined, undefined));
        }
      }
    ),
    takeLatest(Actions.CREATE_WATERMARK_SUCCESS, refetch),
    takeLatest(Actions.CREATE_WATERMARK_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'createWatermarkFailure',
            defaultMessage:
              'Unexpected error occurred while creating the watermark.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}

function* watchUpdate() {
  yield* all([
    takeEvery(
      Actions.UPDATE_WATERMARK_REQUEST,
      function* (
        action: PayloadAction<{
          watermark: { id: string; data: WatermarkDraft };
        }>
      ) {
        const { watermark } = action.payload;
        // Resolved first, for the reason spelled out in `watchCreate`: the
        // replace-artwork branch below mints a TDO, and a failed row write
        // afterwards would strand it.
        const registry = yield* resolveRegistry();
        if (registry.status !== 'ready' && !WATERMARK_LOCAL_FALLBACK_ENABLED) {
          yield* failUnreachable(
            Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined)
          );
          return;
        }
        const previous = yield* findRecord(watermark.id);
        // A NEW raster on an existing row means the operator replaced the
        // artwork, so it needs a TDO of its own — the previous one's assets
        // stay put, since an applied mark may still be resolving through them.
        // Detected by the form carrying an inline raster that the stored row
        // does not: the drawer only sets one when a file was just picked.
        let replaced = watermark.data;
        if (
          watermark.data.type === 'image' &&
          watermark.data.imageBaseDataUrl &&
          watermark.data.imageBaseDataUrl !== previous?.data.imageBaseDataUrl
        ) {
          try {
            replaced = yield* provisionArtwork({
              ...watermark.data,
              // Cleared so `provisionArtwork` treats this as unprovisioned and
              // mints a new TDO rather than returning the old id untouched.
              imageTdoId: undefined,
            });
          } catch (err) {
            console.warn('Watermark artwork could not be stored', err);
            yield* put(Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined));
            return;
          }
        }
        // Merged onto the STORED record, not sent as the whole document. The
        // drawer's form was seeded from the VETTED projection, so any field this
        // client rejected — artwork over the size cap, a preview that is not a
        // PNG — reaches `save` as undefined, and a plain replace would delete it
        // server-side. A rename would quietly strip the row's own image. The
        // drawer sends every field it manages, so the merge changes nothing it
        // meant to change.
        const stamped = yield* stampWriteDefaults(
          { ...previous?.data, ...replaced },
          previous?.data
        );
        if (registry.status === 'ready') {
          yield* put(Services.updateWatermark({ ...watermark, data: stamped }));
          return;
        }
        if (registry.status === 'unreachable') {
          yield* failUnreachable(
            Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined)
          );
          return;
        }
        // Disabled path.
        try {
          const record = yield* call(
            lsUpsertWatermark,
            stamped,
            watermark.id,
            watermark.id,
            new Date().toISOString()
          );
          yield* put(
            Actions.UPDATE_WATERMARK_SUCCESS(
              { createStructuredData: record },
              undefined
            )
          );
        } catch (err) {
          console.warn('Watermark could not be saved locally', err);
          yield* put(Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined));
        }
      }
    ),
    takeLatest(Actions.UPDATE_WATERMARK_SUCCESS, refetch),
    // And on failure too. The Enabled toggle moves optimistically so the switch
    // responds to the click; if the write does not land, the only thing that
    // can put the row back is the server's own answer.
    takeLatest(Actions.UPDATE_WATERMARK_FAILURE, refetch),
    takeEvery(Actions.UPDATE_WATERMARK_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'updateWatermarkFailure',
            defaultMessage:
              'Unexpected error occurred while updating the watermark.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}

const { user: userModule } = modules;

/**
 * Stamp who wrote this, and make the library flags EXPLICIT.
 *
 * `createdBy` is written once and then PRESERVED — an edit must not rewrite the
 * creator, and neither must a soft delete, a restore or an enable toggle, all
 * of which are upserts of the whole record and would otherwise silently reassign
 * authorship to whoever last touched the row.
 *
 * `enabled` and `isDeleted` are written as their defaults rather than left off.
 * This changes no behaviour — absent already reads as enabled and not-deleted
 * everywhere in the selectors, and `?? ` preserves an explicit `true`/`false`
 * from the toggle, the soft delete and the restore. What it changes is whether
 * the row can be FILTERED SERVER-SIDE.
 *
 * Measured on the shared registry: `filter: { isDeleted: "false" }` matches rows
 * that store the key and NOT rows that omit it — 6 records, of which 2 matched
 * `"true"`, 1 matched `"false"` and 3 matched neither. A filtered list query
 * would therefore drop every row nobody had toggled or deleted, silently, which
 * on that registry was half the library. Writing the defaults is what makes the
 * whole library addressable by a filter; the filter itself must not ship until
 * every row has been through here.
 *
 * (String values, not booleans, in any such filter: a boolean literal 500s the
 * SDO endpoint, bare or wrapped in `eq`. The STORED form stays boolean — the
 * backend coerces the string on the way in.)
 *
 * Applied in the saga rather than at each call site because there are four
 * writers now (create, edit, the enable toggle, delete/restore) and these are
 * exactly the kind of field that gets forgotten on the fifth.
 */
function* stampWriteDefaults(
  data: WatermarkDefinitionData,
  previous?: WatermarkDefinitionData
): Generator<unknown, WatermarkDefinitionData, unknown> {
  const user = yield* select(userModule.selectUser);
  const email = user?.email ?? '';
  return {
    ...data,
    enabled: data.enabled ?? true,
    isDeleted: data.isDeleted ?? false,
    // The media flags, written explicitly for the same reason `enabled` and
    // `isDeleted` are: the SDO filter matches stored values exactly, so a row
    // that omits a key matches neither `"true"` nor `"false"` and drops out of
    // any filtered query. Documents default TRUE because it is the only target
    // implemented; the other two default FALSE so nothing offers a watermark
    // for media that will never carry one.
    appliesToDocuments: data.appliesToDocuments ?? true,
    appliesToImages: data.appliesToImages ?? false,
    appliesToVideos: data.appliesToVideos ?? false,
    createdBy: previous?.createdBy || data.createdBy || email || undefined,
    modifiedBy: email || undefined,
  };
}

/**
 * The stored record behind an id, AS THE SERVER SENT IT.
 *
 * `.raw`, not `.data`: the store holds a vetted projection where anything that
 * failed validation is nulled, and every caller here is about to write the
 * record back. Writing the projection makes a read-side defensive control into
 * a destructive server write — an operator flips the Enabled switch on a row
 * whose image this client rejected, and the artwork is gone from the SDO.
 */
function* findRecord(
  id: string
): Generator<unknown, WatermarkDefinition | undefined, unknown> {
  const list = yield* select(selectAllWatermarkRecords);
  const found = list.find((w) => w.id === id);
  return found?.raw ?? found;
}

/**
 * Save a definition through whichever store is live.
 *
 * Factored out because soft delete, restore and an ordinary edit are the same
 * write with different fields — and because the SDO/local branch had already
 * been copied three times, which is how the two sides drift.
 */
function* upsertWatermark(
  raw: WatermarkDefinitionData,
  id: string
): Generator<unknown, void, unknown> {
  // `raw` is spread from the stored record, so its own createdBy is the
  // previous one — passing it as `previous` is what preserves it.
  const data = yield* stampWriteDefaults(raw, raw);
  const registry = yield* resolveRegistry();
  if (registry.status === 'ready') {
    yield* put(Services.updateWatermark({ id, data }));
    return;
  }
  if (registry.status === 'unreachable' || !WATERMARK_LOCAL_FALLBACK_ENABLED) {
    yield* failUnreachable(
      Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined)
    );
    return;
  }
  // Disabled path.
  try {
    const record = yield* call(
      lsUpsertWatermark,
      data,
      id,
      id,
      new Date().toISOString()
    );
    yield* put(
      Actions.UPDATE_WATERMARK_SUCCESS(
        { createStructuredData: record },
        undefined
      )
    );
  } catch (err) {
    console.warn('Watermark could not be saved locally', err);
    yield* put(Actions.UPDATE_WATERMARK_FAILURE(undefined, undefined));
  }
}

/**
 * Best-effort removal of a watermark's container TDO, after its row is gone.
 *
 * Never fails the operation. By the time this runs the SDO row has already been
 * deleted, so the user-visible act — the watermark is gone from the library —
 * has succeeded; raising an error here would report a failure for something
 * that worked, and re-running the delete is not possible from a row that no
 * longer exists. What is left behind is an orphan TDO, which is why this warns
 * with the id rather than swallowing it.
 */
function* deleteArtworkTdo(
  imageTdoId: string | undefined
): Generator<unknown, void, unknown> {
  if (!imageTdoId) {
    return;
  }
  try {
    const cfg = yield* assetConfig();
    yield* call(serviceDeleteWatermarkTdo, imageTdoId, cfg);
  } catch (err) {
    console.warn(
      `[redact] Watermark artwork TDO ${imageTdoId} could not be deleted; ` +
        'it is now orphaned and holds the artwork of a deleted watermark.',
      err
    );
  }
}

function* watchDelete() {
  yield* all([
    /**
     * SOFT delete: an upsert that raises `isDeleted`, not a removal.
     *
     * Safe here in a way it would not be for most records, and for a specific
     * reason: a document that applied this watermark holds its own snapshot
     * (AC#3), so nothing in flight depends on the row surviving. What the flag
     * buys is a restore path and, because the name stops colliding, the freedom
     * to reuse it.
     */
    takeEvery(
      Actions.DELETE_WATERMARK_REQUEST,
      function* (action: PayloadAction<{ id: string }>) {
        const record = yield* findRecord(action.payload.id);
        if (!record) {
          return;
        }
        yield* upsertWatermark({ ...record.data, isDeleted: true }, record.id);
      }
    ),
    takeEvery(
      Actions.SET_WATERMARK_ENABLED,
      function* (action: PayloadAction<{ id: string; enabled: boolean }>) {
        const record = yield* findRecord(action.payload.id);
        if (!record) {
          return;
        }
        yield* upsertWatermark(
          { ...record.data, enabled: action.payload.enabled },
          record.id
        );
      }
    ),
    takeEvery(
      Actions.SET_WATERMARK_AUTO_APPLY,
      function* (action: PayloadAction<{ id: string; autoApply: boolean }>) {
        const record = yield* findRecord(action.payload.id);
        if (!record) {
          return;
        }
        yield* upsertWatermark(
          { ...record.data, autoApply: action.payload.autoApply },
          record.id
        );
      }
    ),
    /**
     * Restore: clear the flag, and nothing else.
     *
     * It used to also compute a `displayOrder` that put the row last. Nothing
     * sorts by `displayOrder` — the table sorts by whichever column the user
     * picked — so that was an arithmetic that looked like a placement decision
     * and made none, on the write path of an action the user is watching.
     * Reinstate it with drag-to-reorder, if that ships.
     */
    takeEvery(
      Actions.RESTORE_WATERMARK_REQUEST,
      function* (action: PayloadAction<{ id: string }>) {
        const record = yield* findRecord(action.payload.id);
        if (!record) {
          return;
        }
        yield* upsertWatermark({ ...record.data, isDeleted: false }, record.id);
      }
    ),
    /**
     * The only path that actually destroys anything. Deleted view only.
     *
     * Order is load-bearing: the SDO row goes FIRST, its container TDO after.
     * A failed TDO delete leaks storage nobody can see; the reverse order would
     * leave a live row whose artwork is gone. Neither is swept, so the leak is
     * logged rather than retried.
     *
     * Safe for documents that already applied this watermark: each holds its
     * own raster asset on its own TDO (§J), so nothing they need is here.
     */
    takeEvery(
      Actions.PERMANENTLY_DELETE_WATERMARK_REQUEST,
      function* (action: PayloadAction<{ id: string }>) {
        const record = yield* findRecord(action.payload.id);
        const imageTdoId = record?.data.imageTdoId;
        const registry = yield* resolveRegistry();
        if (registry.status === 'ready') {
          yield* put(Services.deleteWatermark(action.payload.id));
          yield* deleteArtworkTdo(imageTdoId);
          return;
        }
        if (
          registry.status === 'unreachable' ||
          !WATERMARK_LOCAL_FALLBACK_ENABLED
        ) {
          yield* failUnreachable(
            Actions.DELETE_WATERMARK_FAILURE(undefined, undefined)
          );
          return;
        }
        // Disabled path.
        yield* deleteArtworkTdo(imageTdoId);
        yield* call(lsDeleteWatermark, action.payload.id);
        yield* put(
          Actions.DELETE_WATERMARK_SUCCESS(
            { deleteStructuredData: { id: action.payload.id } },
            undefined
          )
        );
      }
    ),
    takeEvery(Actions.DELETE_WATERMARK_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'deleteWatermarkFailure',
            defaultMessage:
              'Unexpected error occurred while deleting the watermark.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}
